'use client';
import React, { useId, useMemo, useRef, useState } from "react";
import { Calendar } from "lucide-react";
import TrackedLink from "./TrackedLink"; // used for See more + CTA only

// ——— Types ———
type Service = {
  id: string;
  name: string;
  image: string;
  imageAlt: string;
  summary: string;
  description: string;
  ctaLabel: string;
  keywords: string[];
  conditions?: string[];
};

function JsonLd({ services, org }: { services: Service[]; org?: { name: string; url: string } }) {
  const json = useMemo(() => {
    const items = services.map((s, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      item: {
        "@type": "Service",
        "@id": `${org?.url || ""}/nutrition#${s.id}`,
        name: s.name,
        url: `${org?.url || ""}/nutrition#${s.id}`,
        description: s.summary,
        provider: {
          "@type": "LocalBusiness",
          name: org?.name || "CF Organic Wellness",
          url: org?.url || "",
        },
        areaServed: { "@type": "Place", name: "Online & In-clinic" },
        serviceType: s.name,
        image: s.image,
        keywords: (s.keywords || []).join(", "),
      },
    }));

    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: items,
    };
  }, [services, org]);

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }} />;
}

function Modal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  const labelId = useId();
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelId}
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
        <div className="flex items-start justify-between border-b px-6 py-4">
          <h2 id={labelId} className="text-xl font-semibold text-[var(--color-brand)]">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring focus:ring-indigo-500"
            aria-label="Close modal"
            title="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
              <path
                fillRule="evenodd"
                d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 0 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
        <div className="px-6 py-5 text-gray-700">{children}</div>
        <div className="flex justify-end gap-3 border-t px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-xl border px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-700 hover:text-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Helpers to bridge to FloatingCart Quick Actions ----
function openQuickActionsFrom(service: Service) {
  try {
    if (typeof window !== "undefined") {
      (window as { dataLayer?: { push: (data: unknown) => void } }).dataLayer?.push({
        event: "open_quick_actions",
        from: "ServiceCard",
        service_id: service?.id,
        service_name: service?.name,
      });

      window.dispatchEvent(
        new CustomEvent("floatingCart:openQuickActions", {
          detail: { from: "ServiceCard", serviceId: service?.id, serviceName: service?.name },
        })
      );
    }
  } catch {}
}

function ServiceCard({ service, onOpen }: { service: Service; onOpen: (service: Service) => void }) {
  const cardRef = useRef<HTMLDivElement | null>(null);

  return (
    <article
      id={service.id}
      ref={cardRef}
      className="group flex flex-col overflow-hidden rounded-2xl border border-[var(--color-brand)] bg-[var(--color-bg-muted)] shadow-sm transition hover:shadow-md focus-within:shadow-md"
      itemScope
      itemType="https://schema.org/Service"
    >
      {/* Image is now a plain button — no tracking, no rounded styles */}
      <button
        type="button"
        onClick={() => onOpen(service)}
        className="block aspect-[4/3] w-full overflow-hidden bg-gray-100 text-left"
        aria-label={`Read more about ${service.name}`}
      >
        <img
          src={service.image}
          alt={service.imageAlt}
          loading="lazy"
          className="h-full w-full object-cover transition group-hover:scale-105"
          itemProp="image"
        />
      </button>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <h3 className="text-lg font-semibold text-[var(--color-brand)]" itemProp="name">
          {service.name}
        </h3>
        <p className="line-clamp-3 text-sm leading-6 text-[var(--color-text-secondary)]" itemProp="description">
          {service.summary}
        </p>
        <div className="mt-auto flex items-center justify-between pt-2">
          {/* Track only the See more... click for detail opens */}
          <TrackedLink
            onClick={() => onOpen(service)}
            variant="text"
            event="service_more"
            label="See more…"
            component="ServiceCard"
            meta={{ serviceId: service.id }}
            className="text-sm font-medium text-[var(--color-brand)] hover:text-[var(--color-link)]"
          >
            See more…
          </TrackedLink>

          {/* Keep CTA tracked */}
          <TrackedLink
            onClick={() => openQuickActionsFrom(service)}
            variant="solid"
            size="md"
            color="oat"
            event="click"
            label="Open Quick Actions (Service CTA)"
            component="ServiceCard"
            meta={{ serviceId: service.id, serviceName: service.name }}
            className="inline-flex items-center gap-2"
          >
            <Calendar className="h-5 w-5" />
            {service.ctaLabel || "Schedule Now"}
          </TrackedLink>
        </div>
      </div>
      <meta itemProp="serviceType" content={service.name} />
      {service.keywords?.length ? <meta itemProp="keywords" content={service.keywords.join(", ")} /> : null}
    </article>
  );
}

type NutritionServicesGridProps = {
  services: Service[];
  org?: { name: string; url: string };
  heading?: string;
  subheading?: string;
};

export default function NutritionServicesGrid({
  services,
  org,
  heading = "Nutrition Services",
  subheading = "Personalised support for families and individuals",
}: NutritionServicesGridProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<Service | null>(null);

  function openModal(service: Service) {
    setActive(service);
    setOpen(true);
  }

  function closeModal() {
    setOpen(false);
    setActive(null);
  }

  return (
    <section className="mx-auto" aria-labelledby="services-heading">
      <div className="text-center bg-white w-full px-4 sm:px-6 lg:px-8">
        <h2 id="services-heading" className="text-3xl mx-10 font-bold tracking-tight text-[var(--color-brand)] sm:text-4xl">
          {heading}
        </h2>
        <p className="mt-3 text-base text-gray-600 p-5 md:px-20">{subheading}</p>
      </div>

      <div className="mt-8 p-4 grid grid-cols-1 gap-6 sm:gap-8 md:grid-cols-2 lg:grid-cols-3">
        {services.map((s: Service) => (
          <ServiceCard key={s.id} service={s} onOpen={openModal} />
        ))}
      </div>

      <JsonLd services={services} org={org} />

      <Modal open={open} onClose={closeModal} title={active?.name || ""}>
        {active && (
          <div className="prose max-w-none">
            <p className="text-gray-700">{active.description}</p>
            {active.conditions?.length ? (
              <>
                <h3 className="mt-6 text-base font-semibold text-[var(--color-text-brand)]">Common concerns addressed</h3>
                <ul className="list-disc pl-6 text-gray-700">
                  {active.conditions.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </>
            ) : null}
            <div className="mt-6 flex items-center gap-3">
              <TrackedLink
                onClick={() => openQuickActionsFrom(active)}
                variant="solid"
                size="md"
                color="oat"
                event="click"
                label="Modal CTA (Schedule Now)"
                component="ServiceCard.Modal"
                meta={{ serviceId: active.id, serviceName: active.name }}
                className="inline-flex items-center gap-2"
              >
                <Calendar className="h-5 w-5" />
                {active.ctaLabel || "Schedule Now"}
              </TrackedLink>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
}
